import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import { useSmoothScroll } from "../../providers/SmoothScroll";

const BREAK = "@media (max-width:820px)";

// ── Styled ────────────────────────────────────────────────────

const Nav = styled(Box)<{ solid?: string }>(({ solid }) => ({
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  zIndex: 100,
  backgroundColor: solid === "true" ? "rgba(250,246,239,0.72)" : "transparent",
  backdropFilter: solid === "true" ? "blur(12px)" : "none",
  WebkitBackdropFilter: solid === "true" ? "blur(12px)" : "none",
  borderBottom:
    solid === "true" ? "1px solid rgba(191,160,106,0.18)" : "1px solid transparent",
  boxShadow: solid === "true" ? "0 1px 18px rgba(58,53,47,0.07)" : "none",
  transition: "background-color 0.4s ease, box-shadow 0.4s ease, border-color 0.4s ease",
  padding: "10px 20px",
}));

// Barre desktop (cachée sur mobile)
const DesktopBar = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
  [BREAK]: { display: "none" },
});

// Barre mobile (logo + hamburger)
const MobileBar = styled(Box)({
  display: "none",
  [BREAK]: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
});

const NavItem = styled("button")<{ solid?: string }>(({ solid }) => ({
  border: "none",
  background: "none",
  cursor: "pointer",
  fontFamily: "inherit",
  fontSize: "0.68rem",
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  color: solid === "true" ? "#555" : "white",
  padding: "4px 6px",
  transition: "color 0.2s",
  "&:hover": { color: "#BFA06A" },
}));

const Dot = styled("span")<{ solid?: string }>(({ solid }) => ({
  color: solid === "true" ? "#BFA06A" : "rgba(255,255,255,0.6)",
  fontSize: "0.5rem",
  lineHeight: 1,
}));

const Logo = styled("button")({
  width: "42px",
  height: "42px",
  borderRadius: "50%",
  backgroundColor: "#BFA06A",
  color: "white",
  border: "none",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: "0.75rem",
  fontWeight: "bold",
  letterSpacing: "0.05em",
  margin: "0 8px",
  flexShrink: 0,
});

const Burger = styled("button")<{ solid?: string }>(({ solid }) => ({
  border: "none",
  background: "none",
  cursor: "pointer",
  fontSize: "1.5rem",
  lineHeight: 1,
  padding: "4px 8px",
  color: solid === "true" ? "#333" : "white",
}));

// Menu déroulant mobile
const MobileMenu = styled(Box)({
  display: "flex",
  flexDirection: "column" as const,
  alignItems: "center",
  gap: "6px",
  paddingTop: "14px",
  paddingBottom: "6px",
});

const LangSwitch = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "10px",
  marginLeft: "16px",
  [BREAK]: { marginLeft: 0, marginTop: "10px" },
});

const LangButton = styled("button")<{ active?: string }>(({ active }) => ({
  border: "none",
  background: "none",
  cursor: "pointer",
  padding: "2px",
  lineHeight: 0,
  borderRadius: "2px",
  opacity: active === "true" ? 1 : 0.45,
  outline: active === "true" ? "2px solid #BFA06A" : "none",
  transition: "opacity 0.2s",
  "& .fi": { width: "22px", height: "16px", display: "block", borderRadius: "2px" },
  "&:hover": { opacity: 1 },
}));

// ── Données ───────────────────────────────────────────────────
// id = ancre de la section dans la page unique (voir Landing.tsx)

const LEFT_LINKS = [
  { labelKey: "nav.home", id: "home" },
  { labelKey: "nav.story", id: "story" },
  { labelKey: "nav.whereWhen", id: "where-when" },
  { labelKey: "nav.gallery", id: "gallery" },
];

const RIGHT_LINKS = [
  { labelKey: "nav.events", id: "where-when" },
  { labelKey: "nav.rsvp", id: "rsvp" },
];

const ALL_LINKS = [...LEFT_LINKS, ...RIGHT_LINKS];

const LANGS = [
  { code: "fr", flag: "fr", label: "Français" },
  { code: "en", flag: "gb", label: "English" },
  { code: "de", flag: "de", label: "Deutsch" },
];

// ── Composant ─────────────────────────────────────────────────

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const scrollTo = useSmoothScroll();
  const isLanding = location.pathname === "/";
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Solide (blanche) si : pas sur l'accueil, ou défilé, ou menu ouvert
  const solid = !isLanding || scrolled || open ? "true" : "false";

  const go = (id: string) => {
    setOpen(false);
    if (!isLanding) {
      // Sur une autre page : revenir à l'accueil puis l'ancre
      window.location.assign(`/#${id}`);
      return;
    }
    scrollTo(`#${id}`);
  };

  const langButtons = (
    <LangSwitch>
      {LANGS.map((lng) => (
        <LangButton
          key={lng.code}
          type="button"
          title={lng.label}
          aria-label={lng.label}
          onClick={() => i18n.changeLanguage(lng.code)}
          active={(i18n.resolvedLanguage === lng.code).toString()}
        >
          <span className={`fi fi-${lng.flag}`} />
        </LangButton>
      ))}
    </LangSwitch>
  );

  return (
    <Nav solid={solid}>
      {/* ---- Desktop ---- */}
      <DesktopBar>
        {LEFT_LINKS.map((link, i) => (
          <Box key={link.labelKey} sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {i !== 0 && <Dot solid={solid}>♥</Dot>}
            <NavItem solid={solid} onClick={() => go(link.id)}>
              {t(link.labelKey)}
            </NavItem>
          </Box>
        ))}

        <Logo onClick={() => go("home")}>W&D</Logo>

        {RIGHT_LINKS.map((link, i) => (
          <Box key={link.labelKey} sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
            {i !== 0 && <Dot solid={solid}>♥</Dot>}
            <NavItem solid={solid} onClick={() => go(link.id)}>
              {t(link.labelKey)}
            </NavItem>
          </Box>
        ))}

        {langButtons}
      </DesktopBar>

      {/* ---- Mobile ---- */}
      <MobileBar>
        <Logo onClick={() => go("home")}>W&D</Logo>
        <Burger
          solid={solid}
          aria-label="Menu"
          onClick={() => setOpen((o) => !o)}
        >
          {open ? "✕" : "☰"}
        </Burger>
      </MobileBar>

      {open && (
        <MobileMenu>
          {ALL_LINKS.map((link) => (
            <NavItem key={link.labelKey} solid="true" onClick={() => go(link.id)}>
              {t(link.labelKey)}
            </NavItem>
          ))}
          {langButtons}
        </MobileMenu>
      )}
    </Nav>
  );
}
